import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminErrorManagement.css";

const AdminErrorManagement = () => {
  const [errors, setErrors] = useState([]);
  const [filteredErrors, setFilteredErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorsPerPage] = useState(15);
  const [filter, setFilter] = useState("all");

  // Phân trang
  const indexOfLastError = currentPage * errorsPerPage;
  const indexOfFirstError = indexOfLastError - errorsPerPage;
  const currentErrors = filteredErrors.slice(
    indexOfFirstError,
    indexOfLastError
  );
  const totalPages = Math.ceil(filteredErrors.length / errorsPerPage);

  // Lấy dữ liệu lỗi từ Firebase
  useEffect(() => {
    const fetchErrors = async () => {
      setLoading(true);
      try {
        const db = getDatabase(app);
        const errorsRef = ref(db, "errors");
        const snapshot = await get(errorsRef);

        const errorsData = snapshot.val();
        if (errorsData) {
          const errorsList = Object.entries(errorsData)
            .map(([id, data]) => ({
              id,
              ...data,
            }))
            .sort((a, b) => {
              // Phân tích timestamp ISO 8601 và xử lý lỗi
              const dateA = a.timestamp ? new Date(a.timestamp) : null;
              const dateB = b.timestamp ? new Date(b.timestamp) : null;

              const isValidA = dateA && !isNaN(dateA.getTime());
              const isValidB = dateB && !isNaN(dateB.getTime());

              if (isValidA && isValidB) {
                // Cả hai đều hợp lệ, sắp xếp giảm dần (mới nhất trước)
                return dateB.getTime() - dateA.getTime();
              } else if (isValidA) {
                // Chỉ A hợp lệ, A đứng trước B (đẩy B xuống cuối)
                return -1;
              } else if (isValidB) {
                // Chỉ B hợp lệ, B đứng trước A (đẩy A xuống cuối)
                return 1;
              } else {
                // Cả hai đều không hợp lệ, giữ nguyên thứ tự tương đối
                return 0;
              }
            });
          setErrors(errorsList);
          setFilteredErrors(errorsList);
        } else {
          setErrors([]);
          setFilteredErrors([]);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu lỗi:", error);
        setErrors([]);
        setFilteredErrors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchErrors();
  }, []);

  // Lọc lỗi theo từ khóa tìm kiếm và loại lỗi
  useEffect(() => {
    let results = errors;

    // Lọc theo loại lỗi
    if (filter !== "all") {
      results = results.filter((error) => error.errorType === filter);
    }

    // Lọc theo từ khóa tìm kiếm
    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      results = results.filter((error) => {
        return (
          (error.message &&
            error.message.toLowerCase().includes(searchLower)) ||
          (error.errorCode &&
            error.errorCode.toLowerCase().includes(searchLower)) ||
          (error.errorLocation &&
            error.errorLocation.toLowerCase().includes(searchLower)) ||
          (error.id && error.id.toLowerCase().includes(searchLower))
        );
      });
    }

    setFilteredErrors(results);
    setCurrentPage(1);
  }, [searchText, filter, errors]);

  // Xử lý hiển thị chi tiết lỗi
  const handleErrorClick = (error) => {
    setCurrentError(error);
    setShowErrorModal(true);
  };

  // Xử lý xóa lỗi
  const handleDeleteError = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa lỗi này?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const errorRef = ref(db, `errors/${currentError.id}`);

      await remove(errorRef);
      setShowErrorModal(false);

      // Cập nhật state
      setErrors((prevErrors) =>
        prevErrors.filter((error) => error.id !== currentError.id)
      );
    } catch (error) {
      console.error("Lỗi khi xóa thông tin lỗi:", error);
      alert("Đã xảy ra lỗi khi xóa thông tin lỗi!");
    }
  };

  // Xử lý xóa tất cả lỗi
  const handleDeleteAllErrors = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa tất cả lỗi?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const errorsRef = ref(db, "errors");

      await remove(errorsRef);

      // Cập nhật state
      setErrors([]);
      setFilteredErrors([]);
    } catch (error) {
      console.error("Lỗi khi xóa tất cả lỗi:", error);
      alert("Đã xảy ra lỗi khi xóa tất cả lỗi!");
    }
  };

  // Xử lý thay đổi trang
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Lấy danh sách các loại lỗi duy nhất
  const getUniqueErrorTypes = () => {
    const errorTypes = new Set(
      errors
        .map((error) => error.errorType)
        .filter((type) => type !== undefined && type !== "all")
    );
    return ["all", ...Array.from(errorTypes)];
  };

  // Tạo phân trang với dấu chấm lửng
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNeighbours = 1; // Số trang hiển thị ở mỗi bên của trang hiện tại
    const totalNumbers = pageNeighbours * 2 + 3; // Tổng số nút số trang tối đa (1 ... current-1 current current+1 ... total)
    const totalBlocks = totalNumbers + 2; // Tổng số khối tối đa bao gồm cả dấu chấm lửng

    const pageNumbers = [];

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

      pageNumbers.push(1); // Luôn hiển thị trang 1

      if (startPage > 2) {
        pageNumbers.push("..."); // Dấu chấm lửng bên trái
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push("..."); // Dấu chấm lửng bên phải
      }

      pageNumbers.push(totalPages); // Luôn hiển thị trang cuối
    } else {
      // Hiển thị tất cả các trang nếu tổng số trang nhỏ hơn hoặc bằng giới hạn
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    }

    const pages = pageNumbers.map((page, index) => {
      if (page === "...") {
        return (
          <span
            key={`ellipsis-${index}`}
            className="px-3 py-1 mx-1 text-gray-500"
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {page}
        </button>
      );
    });

    return (
      <div className="flex justify-center items-center mt-4 space-x-1">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          aria-label="Trang trước"
        >
          &laquo;
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          aria-label="Trang sau"
        >
          &raquo;
        </button>
      </div>
    );
  };

  // Định dạng hiển thị mức độ nghiêm trọng của lỗi
  const renderErrorSeverity = (error) => {
    const severityMap = {
      Error: {
        label: "Nghiêm trọng",
        className: "bg-red-500",
      },
      Warning: {
        label: "Cảnh báo",
        className: "bg-yellow-500",
      },
      Info: {
        label: "Thông tin",
        className: "bg-blue-500",
      },
      Debug: {
        label: "Debug",
        className: "bg-gray-500",
      },
    };

    const severity = severityMap[error.errorType] || {
      label: "Không xác định",
      className: "bg-purple-500",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${severity.className}`}
      >
        {severity.label}
      </span>
    );
  };

  // Định dạng thời gian
  // Định dạng thời gian theo múi giờ địa phương
  // Định dạng thời gian cố định theo múi giờ GMT+7 (Asia/Saigon)
  const formatDate = (timestamp) => {
    if (!timestamp) return "Không xác định";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.error("Timestamp không hợp lệ:", timestamp);
        return "Thời gian không hợp lệ";
      }

      // Định dạng thời gian (HH:mm) theo múi giờ Asia/Saigon
      const timeFormatter = new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Saigon",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Sử dụng định dạng 24 giờ
      });

      // Định dạng ngày (DD/MM/YYYY) theo múi giờ Asia/Saigon
      const dateFormatter = new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Saigon",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      const timeString = timeFormatter.format(date);
      const dateString = dateFormatter.format(date);

      return `${timeString} ${dateString}`;
    } catch (error) {
      console.error(
        "Lỗi khi định dạng thời gian:",
        error,
        "Timestamp:",
        timestamp
      );
      return "Lỗi định dạng";
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b flex flex-wrap justify-between items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Quản lý lỗi hệ thống
            </h2>
            <div className="flex items-center">
              <button
                onClick={handleDeleteAllErrors}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                Xóa tất cả lỗi
              </button>
            </div>
          </div>

          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tìm kiếm lỗi
                </label>
                <input
                  type="text"
                  placeholder="Tìm kiếm theo nội dung, mã lỗi, vị trí..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loại lỗi
                </label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tất cả loại lỗi</option>
                  {getUniqueErrorTypes()
                    .filter((type) => type !== "all")
                    .map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-600">Đang tải dữ liệu lỗi...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                        <th className="py-3 px-4 text-left">ID</th>
                        <th className="py-3 px-4 text-left">Thời gian</th>
                        <th className="py-3 px-4 text-left">Mã lỗi</th>
                        <th className="py-3 px-4 text-left">Nội dung</th>
                        <th className="py-3 px-4 text-left">Loại lỗi</th>
                        <th className="py-3 px-4 text-left">Vị trí</th>
                        <th className="py-3 px-4 text-center">Hành động</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm">
                      {currentErrors.length > 0 ? (
                        currentErrors.map((error) => (
                          <tr
                            key={error.id}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="py-3 px-4 text-left whitespace-nowrap">
                              {error.id.slice(0, 8)}...
                            </td>
                            <td className="py-3 px-4 text-left whitespace-nowrap">
                              {formatDate(error.timestamp)}
                            </td>
                            <td className="py-3 px-4 text-left max-w-[100px] truncate">
                              {error.errorCode || "N/A"}
                            </td>
                            <td className="py-3 px-4 text-left">
                              <div className="truncate max-w-xs lg:max-w-sm xl:max-w-md">
                                {error.message || "Không có nội dung"}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-left">
                              {renderErrorSeverity(error)}
                            </td>
                            <td className="py-3 px-4 text-left">
                              <div className="truncate max-w-[200px]">
                                {error.errorLocation || "Không xác định"}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => handleErrorClick(error)}
                                className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded-md text-xs transition duration-300"
                              >
                                Chi tiết
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="7"
                            className="py-6 text-center text-gray-500"
                          >
                            Không tìm thấy lỗi nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Chi tiết lỗi */}
      {showErrorModal && currentError && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-lg max-w-4xl w-full mx-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Chi tiết lỗi
              </h3>
              <button
                onClick={() => setShowErrorModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[80vh]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID lỗi
                  </label>
                  <p className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 break-all">
                    {currentError.id}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thời gian
                  </label>
                  <p className="bg-gray-50 px-3 py-2 rounded-md text-gray-700">
                    {formatDate(currentError.timestamp)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mã lỗi
                  </label>
                  <p className="bg-gray-50 px-3 py-2 rounded-md text-gray-700">
                    {currentError.errorCode || "Không có mã lỗi"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loại lỗi
                  </label>
                  <div className="bg-gray-50 px-3 py-2 rounded-md text-gray-700">
                    {renderErrorSeverity(currentError)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vị trí
                  </label>
                  <p className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 break-all">
                    {currentError.errorLocation || "Không xác định"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID người dùng
                  </label>
                  <p className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 break-all">
                    {currentError.userId || "Không có thông tin"}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nội dung lỗi
                </label>
                <div className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 max-h-40 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-mono text-xs">
                    {currentError.message ||
                      "Không có thông tin chi tiết về lỗi"}
                  </pre>
                </div>
              </div>

              {currentError.stackTrace && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stack Trace
                  </label>
                  <div className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 max-h-60 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-mono text-xs">
                      {currentError.stackTrace}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap justify-end gap-2">
              <button
                onClick={handleDeleteError}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Xóa lỗi này
              </button>
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrorManagement;
